import { alpha, Components, createTheme, PaletteOptions, Theme } from '@mui/material/styles';
import { Theme as EmotionTheme } from '@emotion/react';

const primary = {
    0: '#000000',
    10: '#001B3D',
    15: '#002550',
    20: '#003063',
    30: '#22477B',
    40: '#3C5F95',
    50: '#5678B0',
    60: '#7091CB',
    70: '#8BACE7',
    80: '#A9C7FF',
    90: '#D6E3FF',
    95: '#ECF0FF',
    99: '#FAFBFF',
    100: '#FFF'
};

const secondary = {
    0: '#000000',
    10: '#211535',
    20: '#372A4B',
    30: '#4E4163',
    40: '#66587C',
    50: '#7F7196',
    60: '#998AB0',
    70: '#B4A4CC',
    80: '#D0BFE8',
    90: '#ECDCFF',
    95: '#F7EDFF',
    99: '#FDFBFF',
    100: '#FFF'
};

const tertiary = {
    0: '#000000',
    10: '#281900',
    20: '#432C00',
    30: '#604100',
    40: '#7D570E',
    50: '#7C766F',
    60: '#979088',
    70: '#B2AAA2',
    80: '#F1BE6D',
    90: '#FFDEAD',
    95: '#F9EFE6',
    99: '#FEFBF9',
    100: '#FFF'
};

const error = {
    0: '#000000',
    10: '#3B0713',
    20: '#561D27',
    30: '#72333C',
    40: '#D4004B',
    50: '#E61C57',
    60: '#FF4F73',
    70: '#FF8797',
    80: '#FFB2BA',
    90: '#FFD9DC',
    95: '#FFECED',
    99: '#FFFAFB',
    100: '#FFF'
};

const neutral = {
    0: '#000000',
    10: '#1C1B1B',
    20: '#313030',
    30: '#484646',
    40: '#5F5E5E',
    50: '#787776',
    60: '#929090',
    70: '#ADAAAA',
    80: '#C9C6C5',
    90: '#E5E2E1',
    95: '#F4F0EF',
    99: '#FCFBFB',
    100: '#FFF'
};

const neutralVariant = {
    0: '#000000',
    10: '#1B1C1C',
    20: '#303031',
    30: '#474747',
    40: '#5E5E5F',
    50: '#777',
    60: '#919091',
    70: '#ACABAB',
    80: '#C8C6C7',
    90: '#E4E2E2',
    95: '#F2F0F1',
    99: '#FCFBFC',
    100: '#FFF'
};

// For those, where the font is not installed, we will use fallback Arial.
const primaryFont = { style: { fontFamily: 'var(--primary-font), Arial, sans-serif' } };

const defaultTheme: Omit<EmotionTheme, 'palette' | 'spacing'> = {
    colors: {
        primary: primary[40],
        primary15: primary[15],
        primary30: primary[30],
        primary60: primary[60],
        darkSurfaceTint: primary[80],
        lightSurfaceTint: primary[40],
        onPrimary: primary[100],
        primaryDark: primary[80],
        onPrimaryDark: primary[20],
        primaryContainer: primary[90],
        onPrimaryContainer: primary[10],
        primaryContainerDark: primary[30],
        onPrimaryContainerDark: primary[90],
        secondary: secondary[40],
        secondaryDark: secondary[80],
        onSecondary: secondary[100],
        onSecondaryDark: secondary[20],
        secondaryContainer: secondary[95],
        onSecondaryContainer: secondary[90],
        tertiary: tertiary[40],
        onTertiary: tertiary[90],
        tertiaryContainer: tertiary[90],
        onTertiaryContainer: tertiary[10],
        error: error[40],
        onError: error[100],
        errorContainer: error[90],
        onErrorContainer: error[10],
        background: neutral[95],
        onBackground: neutral[10],
        surface: neutral[100],
        onSurface: neutral[10],
        surfaceVariant: neutralVariant[90],
        onSurfaceVariant: neutralVariant[30],
        onSurfaceVariantDark: neutralVariant[80],
        inverseSurface: neutral[20],
        outline: neutralVariant[50],
        outlineVariant: neutralVariant[95],
        // added colors
        disabled: alpha(neutral[10], 0.12),
        disabledText: neutral[60],
        hoverBackground: '#f3f3f3',
        fadedPrimaryContainer: '#f8edf6',
        text: neutral[30],
        gradient1: neutral[100],
        gradient2: '#444444',
        onTertiaryVariant: tertiary[30],
        white: '#ffffff',
        black: '#000000',
        delete: '#8F4953',
        sysDarkOnPrimary: '#09305F',
        sysLightSurfaceTint: '#3C5F95',
        // Comment/Exchange specific colors
        commentText: '#171D1E',
        commentBorder: '#C1C7CE',
        commentPrimary: '#003064',
        commentSecondaryText: '#71787E',
        commentTertiaryText: '#41474D',
        commentDivider: '#E5E7EB',
        overlayBackground: 'rgba(0, 0, 0, 0.5)',
        companiesBackground: secondary[80]
    },
    fonts: {
        primary: primaryFont.style.fontFamily
    },
    zIndex: {
        mobileStepper: 1000,
        fab: 1050,
        speedDial: 1050,
        appBar: 1100,
        drawer: 1200,
        modal: 1300,
        snackbar: 1400,
        tooltip: 1500
    }
};

const generalColors: Partial<PaletteOptions> = {
    primary: {
        main: defaultTheme.colors.primary
    },
    secondary: {
        main: defaultTheme.colors.secondary
    },
    // tertiary
    info: {
        main: defaultTheme.colors.tertiary
    },
    error: {
        main: defaultTheme.colors.error
    },
    common: {
        black: neutral[0],
        white: neutral[100]
    },
    action: { hoverOpacity: 0.08, disabledOpacity: 0.12 }
};

const getComponentTheme = (): Components<Omit<Theme, 'components'>> => ({
    // Overwrite component styles here
    MuiCssBaseline: {
        styleOverrides: {
            html: {
                width: '100%',
                height: '100%',
                minHeight: '100%'
            },
            body: {
                width: '100%',
                height: '100%',
                minHeight: '100%'
            }
        }
    },
    MuiSelect: {
        styleOverrides: {
            standard: {
                height: 40
            },
            select: () => ({
                borderColor: defaultTheme.colors.primary,
                color: defaultTheme.colors.onSurface
            })
        }
    },
    MuiCard: {
        defaultProps: {
            elevation: 0
        },
        styleOverrides: {
            root: {
                width: '100%',
                height: '100%',
                display: 'flex',
                flexDirection: 'column',
                justifyContent: 'center',
                borderRadius: '12px'
            }
        }
    },
    MuiCardContent: {
        styleOverrides: {
            root: {
                '&:last-child': {
                    paddingBottom: 16
                }
            }
        }
    },
    MuiAutocomplete: {
        styleOverrides: {
            noOptions: {
                color: defaultTheme.colors.onSurfaceVariant
            },
            inputRoot: {
                height: 56
            }
        }
    },
    MuiButtonBase: {
        defaultProps: {
            disableRipple: true
        }
    },
    MuiCheckbox: {
        defaultProps: {
            disableRipple: true
        }
    }
});

const muiTheme = createTheme({
    palette: {
        mode: 'light',
        ...generalColors,
        text: {
            primary: defaultTheme.colors.onSurface,
            disabled: defaultTheme.colors.onSurfaceVariant
        },
        background: {
            default: defaultTheme.colors.surface,
            paper: defaultTheme.colors.surface
        },
        common: {
            black: neutral[0],
            white: neutral[100]
        }
    },
    components: getComponentTheme(),
    typography: {
        fontFamily: primaryFont.style.fontFamily,
        fontSize: 14
    },
    breakpoints: {
        values: {
            compact: 0,
            medium: 600,
            expanded: 840
        }
    }
});

// Module Augmentation to be able to add/remove default style attributes from Mui Theme
declare module '@mui/material/styles' {
    interface BreakpointOverrides {
        // removes default breakpoints from theme
        xs: false;
        sm: false;
        md: false;
        lg: false;
        xl: false;
        // add custom breakpoints to theme
        compact: true; // adds the `mobile` breakpoint
        medium: true;
        expanded: true;
    }
}

export { muiTheme, defaultTheme, neutralVariant };
